import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import { DEFAULT_PAGINATION, FILE_NAME_TYPE } from '../../commons/constants/constants';
import { uploadFilesToCms } from '../../services/cmsUploader.service';
import { convertFilesUploadDataToDto } from '../../services/uploader.service';
import { CMSUserContactType } from '../../types/CMSUserContactTypes';
import { PaginationType } from '../../types/CommonTypes';
import { storeName as entityStore } from '../entities/cmsUserContact.entity';
import { RootModel } from '../models';
import { storeName as appStore } from './app.model';

export const storeName = 'cmsUserContacts';

const initialState = {
  ids: [],
  visible: false,
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: {
    keyword: '',
  },
};

const setAvatar = (data, user, uploadActions: Promise<void>[]) => {
  if (data.avatar) {
    const promise = uploadFilesToCms(
      data.code,
      data.avatar.length ? data.avatar : [data.avatar],
      FILE_NAME_TYPE.AVATAR,
      user.id,
      'userContact'
    ).then((result) => {
      convertFilesUploadDataToDto(data, 'avatar', result, false);
    });
    uploadActions.push(promise);
  }
};

const setBackground = (data, user, uploadActions: Promise<void>[]) => {
  if (data.background) {
    const promise = uploadFilesToCms(
      data.code,
      data.background.length ? data.background : [data.background],
      FILE_NAME_TYPE.IMAGE,
      user.id,
      'userContact'
    ).then((result) => {
      convertFilesUploadDataToDto(data, 'background', result, false);
    });
    uploadActions.push(promise);
  }
};

const setLogo = (data, user, uploadActions: Promise<void>[]) => {
  if (data.logo) {
    const promise = uploadFilesToCms(
      data.code,
      data.logo.length ? data.logo : [data.logo],
      FILE_NAME_TYPE.LOGO,
      user.id,
      'userContact'
    ).then((result) => {
      convertFilesUploadDataToDto(data, 'logo', result, false);
    });
    uploadActions.push(promise);
  }
};

export const cmsUserContacts = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof cmsUserContacts>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async filterAsync(payload) {
      this.updateLoading(true);
      try {
        const data: { content: CMSUserContactType[] } & PaginationType = await dispatch[
          entityStore
        ].load(payload);
        if (data && data.content) {
          this.setIds(data.content.map((contact) => contact.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },
    async pagingAsync(payload, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async editASync(payload) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.setVisible(true);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async publishAsync(payload) {
      this.updateLoading(true);
      try {
        await dispatch[entityStore].publish(payload);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async deleteAsync(payload) {
      this.updateLoading(true);
      try {
        await dispatch[entityStore].delete(payload.id);
        await this.reloadList();
        message.warn(`Item ${payload.name} deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete ${payload.name}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async save(payload, state) {
      const { user } = state[appStore];
      const data = payload;
      const uploadActions: Promise<void>[] = [];
      setAvatar(data, user, uploadActions);
      setBackground(data, user, uploadActions);
      setLogo(data, user, uploadActions);
      await axios.all(uploadActions);
      const res = await dispatch[entityStore].save(data);
      return res;
    },
    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const contact = await this.save(payload);
        message.success('Save successfully');
        this.setVisible(false);
        this.setEditId(null);
        this.reloadList();
        return contact;
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }

      return null;
    },
    async saveAndPublishAsync(payload): Promise<void> {
      this.updateLoading(true);
      try {
        const resSave = await this.save(payload);
        if (resSave) {
          await dispatch[entityStore].publish({ id: resSave.id, published: true });
          this.setVisible(false);
          this.setEditId(null);
          this.reloadList();
          message.success('Save and published successfully');
        }
      } catch (err) {
        console.error(err);
        message.error('Fail to save and published');
      } finally {
        this.updateLoading(false);
      }
    },
    async filterPublished() {
      this.updateLoading(true);
      try {
        const data: { content: CMSUserContactType[] } & PaginationType = await dispatch[
          entityStore
        ].loadPublished();
        if (data && data.content) {
          this.setIds(data.content.map((contact) => contact.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to filterPublished');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});

export default cmsUserContacts;
